import { useEffect, useState, useSyncExternalStore } from 'react';
import { onAuthStateChanged, signInWithCustomToken, signOut as authSignOut } from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase';
import { markFreshSignIn } from './deviceStore';

/*
 * Profiles. Sign-in is a six-digit code emailed to a bcm.edu address (Cloud Functions
 * requestSignInCode / verifySignInCode), which signs the device into Firebase Auth and keeps
 * it signed in. The profile is `users/{uid}` in Firestore, mirrored to localStorage so the
 * header and telemetry can read it synchronously and the app works offline.
 */

const MIRROR_KEY = 'profile';
const listeners = new Set();
const readMirror = () => { try { const raw = localStorage.getItem(MIRROR_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; } };
const writeMirror = (p) => { try { if (p) localStorage.setItem(MIRROR_KEY, JSON.stringify(p)); else localStorage.removeItem(MIRROR_KEY); } catch { /* ignore */ } listeners.forEach((l) => l()); };
let mirror = readMirror();
const emit = (p) => { mirror = p; writeMirror(p); };

/** The profile as last known on this device, synchronously; null when signed out */
export const currentProfile = () => mirror;

export const requestCode = async (email) => {
  await httpsCallable(functions, 'requestSignInCode')({ email });
};

/** Signs the device in. Resolves to a sibling address that already has a profile, or null. */
export const verifyCode = async (email, code) => {
  const res = await httpsCallable(functions, 'verifySignInCode')({ email, code });
  markFreshSignIn();
  await signInWithCustomToken(auth, res.data.token);
  return (res.data && res.data.similar) || null;
};

/** Signed in but no profile yet: a sibling address that already has one, or null */
export const checkSimilarProfile = async () => {
  try { const res = await httpsCallable(functions, 'checkSimilarProfile')({}); return (res.data && res.data.similar) || null; } catch { return null; }
};

export const saveProfile = async (fields) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  const ref = doc(db, 'users', user.uid);
  const existing = (await getDoc(ref)).data();
  const data = { ...fields, email: user.email, updatedAt: serverTimestamp(), ...(existing ? {} : { createdAt: serverTimestamp() }) };
  await setDoc(ref, data, { merge: true });
  emit({ ...(existing || {}), ...fields, email: user.email, uid: user.uid });
};

export const signOut = async () => { await authSignOut(auth); emit(null); };

/** Keep the mirror in step with Auth and Firestore. Call once, at the app root. */
export const startProfileSync = () => onAuthStateChanged(auth, (user) => {
  if (!user || !user.email || !/@bcm\.edu$/i.test(user.email)) { if (!user) emit(null); return; }
  const ref = doc(db, 'users', user.uid);
  const stop = onSnapshot(ref, (snap) => {
    emit(snap.exists() ? { ...snap.data(), uid: user.uid, email: user.email } : { uid: user.uid, email: user.email, incomplete: true });
  }, () => { /* offline or rules: keep the mirror */ });
  return stop;
});

/** React hook: the profile, live */
export const useProfile = () => useSyncExternalStore(
  (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
  () => mirror,
  () => null,
);

/** True when Firebase has a signed-in user but no profile document yet (first sign-in) */
export const useAuthUser = () => {
  const [user, setUser] = useState(auth.currentUser);
  useEffect(() => onAuthStateChanged(auth, setUser), []);
  return user;
};

/** Profile site names -> hospital ids on the home page */
const SITE_HOSPITAL = { 'Ben Taub': 'ben-taub', "Baylor St. Luke's": 'st-lukes', 'Texas Heart Institute': 'texas-heart', "Texas Children's": 'texas-childrens', 'VA Medical Center': 'va', 'MD Anderson': 'md-anderson' };
/** The hospital to show first: a resident's current rotation, otherwise the profile's site */
export const homeHospitalId = (profile) => {
  if (!profile || !profile.role) return null;
  const site = profile.role === 'resident' ? profile.rotation : profile.site;
  return SITE_HOSPITAL[site] || null;
};

export const describeProfileError = (e) => {
  const code = (e && e.code) || '';
  if (/invalid-argument|permission-denied|deadline-exceeded|resource-exhausted|unavailable/.test(code)) return e.message.replace(/^.*?:\s*/, '');
  return 'Something went wrong. Try again in a moment.';
};
